import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { FiRefreshCw, FiSave, FiLock } from "react-icons/fi";

export default function ConversionSettings() {
  const [loading, setLoading] = useState(true);

  const adminUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isAuthorized = useMemo(() => {
    const allowedEmails = ["admin@arrowcoders.com", "haravindh@arrowcoders.com"];
    return allowedEmails.includes(adminUser?.email?.toLowerCase());
  }, [adminUser]);

  const [settings, setSettings] = useState({
    butterfliesToRupees: 10,
    minimumWithdrawal: 50,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/v1/app/conversion/settings");

      if (response.data.status === "OK") {
        const s = response.data.data.settings;

        // MAP BACKEND → FRONTEND
        setSettings({
          coinsToRupees: s.coinToRupeesRate,
          minimumWithdrawal: s.minumumWithDrawalAmount,
        });
      }
    } catch (error) {
      console.error("Error fetching conversion settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // MAP FRONTEND → BACKEND
      const payload = {
        coinToRupeesRate: settings.coinsToRupees,
        minumumWithDrawalAmount: settings.minimumWithdrawal,
      };

      const response = await axios.put(
        "/api/v1/app/conversion/settings",
        payload
      );

      if (response.data.status === "OK") {
        alert("Settings saved successfully!");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--black)] p-6">
        <div className="text-center p-12 bg-[var(--black)] rounded-2xl shadow-2xl border-2 border-[var(--yellow)] max-w-lg">
          <div className="text-[var(--yellow)] text-6xl mb-6 flex justify-center animate-pulse">
            <FiLock />
          </div>
          <h2 className="text-3xl font-bold text-[var(--yellow)] mb-4">Access Restricted</h2>
          <p className="text-[color:var(--yellow)/0.8] text-lg leading-relaxed">
            This high-security configuration page is restricted to authorized administrators only. 
            If you require access, please contact the lead developer or super admin.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 bg-[var(--black)] min-h-[200px]">
        <div className="flex justify-center items-center h-40">
          <div className="w-8 h-8 rounded-full animate-spin border-4 border-[color:var(--yellow)/0.25] border-t-[var(--yellow)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-[var(--black)] min-h-screen text-[var(--yellow)]">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-3 text-[var(--yellow)]">
          <FiRefreshCw className="text-[var(--yellow)]" />
          Conversion Settings
        </h1>
      </div>

      <div className="bg-[var(--black)] border-2 border-gray-300 rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-6 text-[var(--yellow)]">
          Butterflies to Rupee Conversion
        </h2>

        <div className="space-y-4">
          {/* Coins to Rupee Rate */}
          <div>
            <label className="block text-sm font-medium text-[color:var(--yellow)/0.9] mb-2">
              Butterflies to Rupees Rate
            </label>
            <input
              type="number"
              value={settings.coinsToRupees}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  coinsToRupees: parseFloat(e.target.value),
                })
              }
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-[var(--black)] text-[var(--yellow)] focus:outline-none focus:ring-2 focus:ring-[color:var(--yellow)/0.6]"
            />
            <p className="text-sm text-[color:var(--yellow)/0.8] mt-1">
              {settings.coinsToRupees} Butterflies = 1 Rupee
            </p>
          </div>

          {/* Minimum Withdrawal */}
          <div>
            <label className="block text-sm font-medium text-[color:var(--yellow)/0.9] mb-2">
              Minimum Withdrawal Amount (Rupees)
            </label>
            <input
              type="number"
              value={settings.minimumWithdrawal}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  minimumWithdrawal: parseFloat(e.target.value),
                })
              }
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-[var(--black)] text-[var(--yellow)] focus:outline-none focus:ring-2 focus:ring-[color:var(--yellow)/0.6]"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--yellow)] text-[var(--black)] rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave />
            <span>{saving ? "Saving..." : "Save Settings"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
